import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  ArrowRight,
  Lock,
  ShieldCheck,
  Clock,
  Trophy,
  ThumbsUp,
  Gauge,
  ChevronDown,
} from "lucide-react";
import { Footer } from "@/components/laneguide/Footer";
import { LoanWarning } from "@/components/laneguide/LoanWarning";
import { LenderLogo } from "@/components/laneguide/LenderLogo";
import { StarRating } from "@/components/laneguide/StarRating";
import { applyUrl, type Lender } from "@/lib/lenders";
import {
  matchPartners,
  ucBucketMin,
  KFM_NOTICE,
  OVER_MAX_NOTICE,
  displayChance,
  type MatchResult,
  type Chance,
} from "@/lib/partnerMatching";
import { AmountInput } from "@/components/laneguide/AmountInput";
import { submitLead } from "@/lib/leads.functions";
import { EpiProvider } from "@/lib/epi";

import logo from "@/assets/logo-header.webp";

export const Route = createFileRoute("/lp/lanekalkyl")({
  head: () => ({
    meta: [
      { title: "Lånekalkyl – räkna på dina lån och krediter | Svensk Låneguide" },
      {
        name: "description",
        content:
          "Fyll i dina befintliga lån och se total månadskostnad, snittränta, effektiv ränta och vad nästa steg bör vara.",
      },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Lånekalkyl – räkna på dina lån" },
      { property: "og:description", content: "Se din totala månadskostnad, snittränta och nästa steg." },
      { property: "og:type", content: "website" },
    ],
  }),
  component: Page,
});

const fmt = (n: number) => new Intl.NumberFormat("sv-SE").format(Math.round(n));
const fmtRate = (n: number) => n.toFixed(2).replace(".", ",");

const EMAIL_RE = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;
const isValidEmail = (v: string) => EMAIL_RE.test(v.trim());
const isValidPhone = (v: string) => {
  const d = v.replace(/[^\d+]/g, "").replace(/^\+46/, "0");
  return /^0\d{8,11}$/.test(d);
};

type LoanRow = { lender: string; amount: number; rate: number; monthly: number };

const chanceScore = (c: Chance) => (c === "Hög" ? 2 : c === "Medel" ? 1 : 0);

/** Lägsta ränta i långivarens räntespann, t.ex. "4,95 – 23 %" -> 4.95 */
function fromRate(l: Lender): number {
  const m = l.rate.replace(",", ".").match(/\d+(\.\d+)?/);
  return m ? Number(m[0]) : 9;
}

/** Annuitetsberäkning */
function annuity(amount: number, yearlyRate: number, months: number) {
  const r = yearlyRate / 100 / 12;
  if (amount <= 0 || months <= 0) return 0;
  if (r === 0) return amount / months;
  return (amount * r) / (1 - Math.pow(1 + r, -months));
}

/** Uppskattad ny månadskostnad vid samlingslån hos långivaren. */
function consolidatedMonthly(l: Lender, totalDebt: number, avgRate: number) {
  const rate = Math.min(avgRate > 0 ? avgRate : 99, fromRate(l) + 3);
  return annuity(totalDebt, rate, 144);
}

function pickLenders(
  totalDebt: number,
  remarks: boolean,
  kfm: boolean,
  ucCount: string,
): MatchResult {
  return matchPartners({
    amount: totalDebt,
    remarks,
    kfm,
    ucMin: ucBucketMin(ucCount || null),
    income: null, // formuläret frågar inte efter inkomst
  });
}

/** Effektiv ränta (approx) utifrån total månadskostnad och total skuld. */
function effectiveRate(totalDebt: number, totalMonthly: number, avgRate: number) {
  if (totalDebt <= 0 || totalMonthly <= 0) return avgRate;
  const monthly = avgRate / 100 / 12;
  return (Math.pow(1 + monthly, 12) - 1) * 100;
}


function Page() {
  const [count, setCount] = useState(2);
  const [loans, setLoans] = useState<LoanRow[]>(
    Array.from({ length: 6 }, () => ({ lender: "", amount: 0, rate: 0, monthly: 0 })),
  );
  const [bulkAmount, setBulkAmount] = useState(0);
  const [bulkMonthly, setBulkMonthly] = useState(0);
  const [bulkRate, setBulkRate] = useState(0);
  const [remarks, setRemarks] = useState<boolean | null>(null);
  const [kfm, setKfm] = useState<boolean | null>(null);
  const [ucCount, setUcCount] = useState<string>("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [touched, setTouched] = useState<{ email?: boolean; phone?: boolean }>({});

  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [overrideAmount, setOverrideAmount] = useState<number | null>(null);

  const bulk = count === 7;
  const active = bulk
    ? [{ lender: "Flera lån & krediter", amount: bulkAmount, rate: bulkRate, monthly: bulkMonthly }]
    : loans.slice(0, count);
  const loanCount = bulk ? 7 : count;
  const totalDebt = bulk ? bulkAmount : active.reduce((s, l) => s + (l.amount || 0), 0);
  const totalMonthly = bulk ? bulkMonthly : active.reduce((s, l) => s + (l.monthly || 0), 0);
  const avgRate = bulk
    ? bulkRate
    : totalDebt > 0
      ? active.reduce((s, l) => s + (l.rate || 0) * (l.amount || 0), 0) / totalDebt
      : 0;
  const effRate = effectiveRate(totalDebt, totalMonthly, avgRate);
  const yearlyInterest = (totalDebt * avgRate) / 100;
  const multi = loanCount >= 2;

  const match = useMemo(
    () => pickLenders(totalDebt, remarks === true, kfm === true, ucCount),
    [totalDebt, remarks, kfm, ucCount],
  );
  const overMax = match.kind === "over-max";
  // Vid belopp över 800 000 kr kan kunden själv dra ner beloppet (max 800 000)
  // och få förslag baserade på samma övriga svar.
  const effectiveMatch = useMemo(() => {
    if (!overMax) return match;
    if (!overrideAmount || overrideAmount > 800000) return match;
    return pickLenders(overrideAmount, remarks === true, kfm === true, ucCount);
  }, [overMax, overrideAmount, match, remarks, kfm, ucCount]);
  const results = effectiveMatch.lenders;
  const showResults = !overMax || (overrideAmount !== null && overrideAmount <= 800000);
  const topPick = results[0];

  const emailOk = isValidEmail(email);
  const phoneOk = isValidPhone(phone);
  const canSubmit =
    totalDebt > 0 && remarks !== null && kfm !== null && firstName && lastName && emailOk && phoneOk;


  const setLoan = (i: number, p: Partial<LoanRow>) =>
    setLoans((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...p } : l)));

  async function onSubmit() {
    if (!canSubmit || loading) return;
    setLoading(true);
    setDone(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
    try {
      void submitLead({
        data: {
          source: "lp/lanekalkyl",
          name: `${firstName} ${lastName}`.trim(),
          email,
          phone,
          amount: Math.round(totalDebt),
          goal: "lanekalkyl",
          debtTypes: active.map((l) => l.lender || "Okänd långivare"),
          kfm,
          remarks,
          ucCount: ucCount || null,
          matchedLenders: results.map((l) => l.id),

        },
      }).catch(() => undefined);

    } finally {
      setLoading(false);
    }
  }

  return (
    <EpiProvider value="lp-lanekalkyl">
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <header className="bg-[#0b3d91] text-white">
        <div className="mx-auto flex max-w-6xl items-center justify-center px-4 py-4">
          <img src={logo} alt="Svensk Låneguide" width={290} height={100} className="h-8 w-auto brightness-0 invert" />
        </div>
        <div className="bg-[#12539f]">
          <div className="mx-auto max-w-3xl px-4 py-14 text-center sm:py-16">
            <h1 className="font-display text-4xl font-extrabold tracking-tight sm:text-5xl">
              Räkna på <span className="text-[#7fd3ff]">dina lån</span>
            </h1>
            <p className="mt-3 text-[15px] text-white/85">
              Se vad du betalar per månad, din snittränta och vad nästa steg bör vara.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3 text-sm font-semibold">
              {[
                { icon: ArrowRight, t: "Ingen kreditupplysning i detta steg" },
                { icon: ShieldCheck, t: "Kostnadsfri tjänst" },
                { icon: Clock, t: "Svar inom 2 minuter" },
              ].map(({ icon: Icon, t }) => (
                <span
                  key={t}
                  className="flex items-center gap-2 rounded-full bg-white/12 px-4 py-2 backdrop-blur-sm"
                >
                  <span className="grid h-6 w-6 place-items-center rounded-full bg-white/20">
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  {t}
                </span>
              ))}
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
        {!done ? (
          <div className="rounded-md border border-border bg-card p-5 shadow-elevated sm:p-8">
            <h2 className="font-display text-2xl font-extrabold text-foreground">Din lånekalkyl</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Fyll i dina befintliga lån och krediter – vi räknar ut resten.
            </p>

            {/* 1. UC senaste 12 mån */}
            <div className="mt-6">
              <label className="text-sm font-semibold text-foreground">
                1. Antal kreditupplysningar senaste 12 månaderna
              </label>
              <div className="mt-2 flex flex-wrap gap-2">
                {["0", "1-2", "3-5", "6-10", "10+"].map((o) => (
                  <button
                    key={o}
                    type="button"
                    onClick={() => setUcCount(o)}
                    className={`h-11 rounded-md border px-4 text-sm font-bold transition ${
                      ucCount === o
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-card text-foreground hover:border-primary/50"
                    }`}
                  >
                    {o}
                  </button>
                ))}
              </div>
            </div>

            {/* 2. antal lån */}
            <div className="mt-6">
              <label className="text-sm font-semibold text-foreground">
                2. Antal befintliga lån &amp; krediter
              </label>

              <div className="mt-2 flex flex-wrap gap-2">
                {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setCount(n)}
                    className={`h-11 rounded-md border px-3 text-sm font-bold transition ${
                      n === 7 ? "min-w-[3.25rem]" : "w-11"
                    } ${
                      count === n
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-card text-foreground hover:border-primary/50"
                    }`}
                  >
                    {n === 7 ? "7+" : n}
                  </button>
                ))}
              </div>
            </div>

            {/* 3. lånefält */}
            <div className="mt-6 space-y-4">
              <div className="text-sm font-semibold text-foreground">3. Dina lån</div>

              {bulk ? (
                <div className="rounded-md border border-border bg-muted/30 p-4">
                  <div className="mb-3 text-xs font-bold uppercase tracking-wider text-primary">
                    Sammanlagt för alla dina lån
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <NumInput label="Totalt belopp (kr)" value={bulkAmount} onChange={setBulkAmount} />
                    <NumInput
                      label="Total månadskostnad (kr)"
                      value={bulkMonthly}
                      onChange={setBulkMonthly}
                    />
                    <NumInput label="Snittränta (%)" value={bulkRate} decimals onChange={setBulkRate} />
                  </div>
                </div>
              ) : (
                active.map((l, i) => (
                <div key={i} className="rounded-md border border-border bg-muted/30 p-4">
                  <div className="mb-3 text-xs font-bold uppercase tracking-wider text-primary">Lån {i + 1}</div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Input
                      label="Långivare"
                      value={l.lender}
                      onChange={(v) => setLoan(i, { lender: v })}
                      placeholder="t.ex. Nordea"
                    />
                    <NumInput
                      label="Skuld (kr)"
                      value={l.amount}
                      onChange={(v) => setLoan(i, { amount: v })}
                    />
                    <NumInput
                      label="Ränta (%)"
                      value={l.rate}
                      decimals
                      onChange={(v) => setLoan(i, { rate: v })}
                    />
                    <NumInput
                      label="Månadskostnad (kr)"
                      value={l.monthly}
                      onChange={(v) => setLoan(i, { monthly: v })}
                    />
                  </div>
                </div>
                ))
              )}
            </div>


            {/* 4 & 5 */}
            <div className="mt-6 grid gap-5 sm:grid-cols-2">
              <YesNo
                label="4. Har du betalningsanmärkningar?"
                value={remarks}
                onChange={setRemarks}
              />
              <YesNo label="5. Har du skulder hos Kronofogden?" value={kfm} onChange={setKfm} />
            </div>

            {/* 6. kontakt */}
            <div className="mt-6">
              <div className="text-sm font-semibold text-foreground">6. Dina uppgifter</div>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <Input label="Förnamn" value={firstName} onChange={setFirstName} placeholder="Anna" />
                <Input label="Efternamn" value={lastName} onChange={setLastName} placeholder="Andersson" />
                <Input
                  label="E-post"
                  value={email}
                  onChange={setEmail}
                  onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                  placeholder="anna@example.se"
                  type="email"
                  error={touched.email && !emailOk ? "Ange en giltig e-post, t.ex. anna@example.se" : undefined}
                />
                <Input
                  label="Mobilnummer"
                  value={phone}
                  onChange={setPhone}
                  onBlur={() => setTouched((t) => ({ ...t, phone: true }))}
                  placeholder="070-123 45 67"
                  type="tel"
                  error={touched.phone && !phoneOk ? "Ange ett giltigt mobilnummer, t.ex. 070-123 45 67" : undefined}
                />
              </div>
            </div>


            <button
              onClick={onSubmit}
              disabled={!canSubmit}
              className="mt-7 flex w-full items-center justify-center gap-2 rounded-md bg-cta py-4 text-base font-bold text-cta-foreground transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Räkna hur mycket jag betalar
              <ArrowRight className="h-5 w-5" />
            </button>
            <p className="mt-3 flex items-center justify-center gap-2 text-xs text-muted-foreground">
              <Lock className="h-3.5 w-3.5 text-primary" /> Ingen kreditupplysning tas i detta steg.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="rounded-md border border-border bg-card p-5 shadow-elevated sm:p-7">
              <h2 className="font-display text-2xl font-extrabold text-foreground">
                Din situation, {firstName}
              </h2>
              <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-3">
                <Stat label="Total skuld" value={`${fmt(totalDebt)} kr`} big />
                <Stat label="Total månadskostnad" value={`${fmt(totalMonthly)} kr`} big />
                <Stat label="Snittränta" value={`${fmtRate(avgRate)} %`} big />
                <Stat label="Effektiv ränta (ca)" value={`${fmtRate(effRate)} %`} big />
                <Stat label="Räntekostnad per år" value={`${fmt(yearlyInterest)} kr`} big />
                <Stat label="Antal lån" value={`${active.length} st`} big />
              </div>



              <div className="mt-5 overflow-x-auto">
                <table className="w-full min-w-[480px] text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                      <th className="py-2">Långivare</th>
                      <th className="py-2">Skuld</th>
                      <th className="py-2">Ränta</th>
                      <th className="py-2">Per månad</th>
                    </tr>
                  </thead>
                  <tbody>
                    {active.map((l, i) => (
                      <tr key={i} className="border-b border-border/60">
                        <td className="py-2 font-semibold text-foreground">{l.lender || `Lån ${i + 1}`}</td>
                        <td className="py-2">{fmt(l.amount)} kr</td>
                        <td className="py-2">{fmtRate(l.rate)} %</td>
                        <td className="py-2">{fmt(l.monthly)} kr</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button
                type="button"
                onClick={() =>
                  document.getElementById("forslag")?.scrollIntoView({ behavior: "smooth", block: "start" })
                }
                className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground transition hover:brightness-105"
              >
                Se förslag
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>

            {multi && (
              <div className="rounded-md border border-border bg-card p-5 shadow-card sm:p-6">
                <h3 className="font-display text-lg font-extrabold text-foreground">
                  Gå från {loanCount === 7 ? "flera" : loanCount} fakturor till 1 – så funkar ett samlingslån
                </h3>
                <ol className="mt-4 space-y-3">
                  {[
                    "Ansök om ett samlingslån & signera",
                    "Få hela summan på bankkontot",
                    "Betala av de gamla lånen (flera banker gör detta automatiskt)",
                    `Du får 1 faktura varje månad istället för ${loanCount === 7 ? "flera" : loanCount}`,
                  ].map((t, i) => (
                    <li key={t} className="flex items-start gap-3 text-sm text-foreground/85">
                      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                        {i + 1}
                      </span>
                      {t}
                    </li>
                  ))}
                </ol>
              </div>
            )}


            {/* Topplista */}
            <div id="forslag" className="scroll-mt-6">
              <h2 className="font-display text-2xl font-extrabold text-foreground">Nästa steg – dina bästa förslag</h2>
              {effectiveMatch.kind === "kfm" && (
                <p className="mt-2 rounded-md border border-border bg-muted/40 px-4 py-3 text-sm leading-relaxed text-foreground/85">
                  {KFM_NOTICE}
                </p>
              )}
              {overMax && (
                <div className="mt-2 rounded-md border border-border bg-muted/40 px-4 py-4">
                  <p className="text-sm leading-relaxed text-foreground/85">{OVER_MAX_NOTICE}</p>
                  <p className="mt-1 text-sm leading-relaxed text-foreground/85">
                    Dra i reglaget eller skriv ett eget belopp upp till 800 000 kr – då söker vi på nytt med dina
                    övriga svar och visar rätt förslag.
                  </p>
                  <div className="mt-4 text-center font-display text-3xl font-extrabold text-primary">
                    {fmt(overrideAmount ?? 800000)} kr
                  </div>
                  <input
                    type="range"
                    min={1000}
                    max={800000}
                    step={1000}
                    value={overrideAmount ?? 800000}
                    onChange={(e) => setOverrideAmount(Number(e.target.value))}
                    style={{ ["--val" as string]: `${(((overrideAmount ?? 800000) - 1000) / 799000) * 100}%` } as React.CSSProperties}
                    className="range-slider mt-3 w-full"
                  />
                  <div className="mt-1 flex justify-between text-[11px] font-medium text-muted-foreground">
                    <span>1 000 kr</span>
                    <span>800 000 kr</span>
                  </div>
                  <div className="mt-3 flex items-center gap-2 rounded-md border border-input bg-card px-3 py-2">
                    <AmountInput
                      value={overrideAmount ?? 800000}
                      onChange={(n) => setOverrideAmount(Math.min(n, 800000))}
                      max={800000}
                      className="w-full bg-transparent text-[15px] outline-none"
                    />
                    <span className="text-sm font-semibold text-muted-foreground">kr</span>
                  </div>
                </div>
              )}
              {!overMax && (
              <p className="mt-1 text-sm text-muted-foreground">
                Sannolikheten avser chansen att få ett lån beviljat och är en indikation baserad på dina svar – inte
                ett beslut från långivaren.
              </p>
              )}
              {showResults && (
              <div className="mt-4 space-y-3">
                {results.map((l, i) => {
                  const chance: Chance = displayChance(
                    i,
                    remarks === true,
                    kfm === true,
                    l,
                    totalDebt,
                  );
                  const newMonthly = consolidatedMonthly(l, totalDebt, avgRate);
                  const showConsolidation = multi && totalDebt > 0 && newMonthly > 0;
                  const isOpen = open === l.id;
                  return (
                    <div
                      key={l.id}
                      className={`rounded-md border bg-card p-4 shadow-card sm:p-5 ${
                        i === 0 ? "border-primary" : "border-border"
                      }`}
                    >
                      {i === 0 && (
                        <div className="mb-3 inline-flex items-center gap-1.5 rounded-md bg-primary px-2.5 py-1 text-xs font-bold text-primary-foreground">
                          <Trophy className="h-3.5 w-3.5" /> Toppval för dig
                        </div>
                      )}
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                        <div className="flex min-w-0 items-center gap-3 sm:gap-4">
                          <div className="shrink-0">
                            <LenderLogo lender={l} size={90} bare />
                          </div>
                          <div className="min-w-0">
                            <div className="font-display text-base font-extrabold text-foreground sm:text-lg">
                              {l.name}
                            </div>
                            <StarRating rating={l.rating} />
                            <div className="mt-1 text-xs leading-snug text-muted-foreground">
                              {l.rate}
                              <span className="hidden sm:inline"> · {l.term}</span>
                            </div>
                            <div className="text-xs leading-snug text-muted-foreground sm:hidden">{l.term}</div>
                          </div>
                        </div>
                        <div className="flex flex-col gap-3 sm:items-end">
                          <div className="flex items-center gap-2">
                            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                              Sannolikhet
                            </div>
                            <div
                              className={`font-display text-base font-extrabold leading-none ${
                                chance === "Hög"
                                  ? "text-cta"
                                  : chance === "Medel"
                                    ? "text-primary"
                                    : "text-destructive"
                              }`}
                            >
                              {chance}
                            </div>
                          </div>
                          <a
                            href={applyUrl(l, "lp-lanekalkyl", "result-card")}
                            target="_blank"
                            rel="nofollow sponsored noopener"
                            className="block whitespace-nowrap rounded-md bg-cta px-5 py-3 text-center text-sm font-bold text-cta-foreground hover:brightness-105"
                          >
                            Ansök nu
                          </a>
                        </div>
                      </div>

                      {showConsolidation && (
                        <div className="mt-4 rounded-md bg-muted/50 px-4 py-3 text-sm">
                          <div className="font-semibold text-foreground">
                            Vid samlingslån på {fmt(totalDebt)} kr:
                          </div>
                          <div className="mt-0.5 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                            <span className="font-display text-lg font-extrabold text-primary">
                              {fmt(newMonthly)} kr/mån
                            </span>
                            <span className="text-muted-foreground/70 line-through">{fmt(totalMonthly)} kr/mån</span>
                          </div>
                          <div className="mt-1 text-xs leading-snug text-muted-foreground">
                            Uppskattning på 12 års löptid utifrån långivarens räntespann. Din faktiska ränta sätts
                            individuellt.
                          </div>
                        </div>
                      )}



                      <button
                        type="button"
                        onClick={() => setOpen(isOpen ? null : l.id)}
                        className="mt-3 flex items-center gap-1.5 text-sm font-semibold text-primary"
                      >
                        Läs mer
                        <ChevronDown className={`h-4 w-4 transition ${isOpen ? "rotate-180" : ""}`} />
                      </button>
                      {isOpen && (
                        <div className="mt-3 rounded-md border border-border bg-muted/30 p-4">
                          <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
                            <Row k="Belopp" v={`${fmt(l.minAmount)} – ${fmt(l.maxAmount)} kr`} />
                            <Row k="Ränta" v={l.rate} />
                            <Row k="Löptid" v={l.term} />
                            <Row k="Utan UC" v={l.usesUc ? "Nej" : "Ja"} />
                            <Row k="Betalningsanmärkning" v={l.allowsRemarks ? "Godkänns" : "Godkänns ej"} />
                            <Row k="Åldersgräns" v={`${l.minAge} år`} />
                            <Row k="Uppläggningsavgift" v={l.setupFee} />
                            <Row k="Administrationsavgift" v={l.adminFee} />
                            <Row k="Fakturaavgift" v={l.invoiceFee} />
                          </dl>
                          <p className="mt-3 text-xs leading-relaxed text-muted-foreground">{l.example}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              )}

            </div>

            <LoanWarning />

            <button
              onClick={() => setDone(false)}
              className="text-sm font-semibold text-primary underline"
            >
              Gör om kalkylen
            </button>
          </div>
        )}
      </main>

      <AboutService />
      <Footer />
    </div>
    </EpiProvider>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-border/60 py-1.5">
      <dt className="text-muted-foreground">{k}</dt>
      <dd className="text-right font-semibold text-foreground">{v}</dd>
    </div>
  );
}

function Stat({ label, value, big }: { label: string; value: string; big?: boolean }) {

  return (
    <div className="rounded-md bg-card/70 px-3 py-2">
      <div className="text-[11px] font-semibold uppercase leading-tight tracking-wider text-muted-foreground">{label}</div>
      <div className={`font-display font-extrabold leading-tight text-primary ${big ? "text-xl sm:text-2xl" : "text-lg"}`}>{value}</div>

    </div>
  );
}

function Input({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  onBlur,
  error,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  onBlur?: () => void;
  error?: string;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-foreground">{label}</span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        aria-invalid={error ? true : undefined}
        className={`w-full rounded-md border bg-card px-3 py-2.5 text-[15px] outline-none transition focus:ring-4 ${
          error
            ? "border-destructive focus:border-destructive focus:ring-destructive/10"
            : "border-input focus:border-primary focus:ring-primary/10"
        }`}
      />
      {error && <span className="mt-1 block text-xs font-medium text-destructive">{error}</span>}
    </label>
  );
}


function NumInput({
  label,
  value,
  onChange,
  decimals,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  decimals?: boolean;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-foreground">{label}</span>
      <input
        type="text"
        inputMode="decimal"
        value={value === 0 ? "" : String(value).replace(".", ",")}
        placeholder="0"
        onChange={(e) => {
          const raw = e.target.value.replace(",", ".").replace(decimals ? /[^\d.]/g : /[^\d]/g, "");
          onChange(Number(raw) || 0);
        }}
        className="w-full rounded-md border border-input bg-card px-3 py-2.5 text-[15px] outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
      />
    </label>
  );
}

function YesNo({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean | null;
  onChange: (v: boolean) => void;
}) {
  return (
    <div>
      <div className="text-sm font-semibold text-foreground">{label}</div>
      <div className="mt-2 grid grid-cols-2 gap-2">
        {[
          { v: false, t: "Nej" },
          { v: true, t: "Ja" },
        ].map((o) => (
          <button
            key={o.t}
            type="button"
            onClick={() => onChange(o.v)}
            className={`rounded-md border py-3 text-sm font-semibold transition ${
              value === o.v
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border bg-card text-foreground hover:border-primary/50"
            }`}
          >
            {o.t}
          </button>
        ))}
      </div>
    </div>
  );
}

const ABOUT = [
  {
    icon: Trophy,
    title: "Kostnadsfri tjänst",
    text: "Vår tjänst är helt kostnadsfri för dig som användare. Vi hjälper dig jämföra låneerbjudanden utan några dolda avgifter.",
  },
  {
    icon: ThumbsUp,
    title: "Matchas med rätt lån",
    text: "Vi är inte själva långivare utan samarbetar med över 30 banker och kreditgivare för att hjälpa dig hitta det bästa lånet för just dina behov.",
  },
  {
    icon: Gauge,
    title: "Snabba svar",
    text: "Genom vår tjänst får du låneförslag inom 2 minuter. Pengarna kan finnas på ditt konto redan samma dag hos vissa långivare.",
  },
];

const HOW = [
  "Fyll i kalkylen med dina lån och dina uppgifter.",
  "Vi räknar ut din totala månadskostnad, snittränta och effektiva ränta.",
  "Du får en AI-sammanfattning av din situation och nästa steg.",
  "Ansökan sker sedan tryggt och säkert online hos våra partners.",
];

function AboutService() {
  return (
    <section className="bg-muted/40 py-12">
      <div className="mx-auto max-w-3xl px-4 sm:px-6">
        <div className="rounded-md border border-border bg-card p-6 shadow-card sm:p-8">
          <h2 className="text-center font-display text-2xl font-extrabold text-foreground">Om vår tjänst</h2>
          <div className="mt-8 grid gap-8 sm:grid-cols-3">
            {ABOUT.map(({ icon: Icon, title, text }) => (
              <div key={title} className="text-center">
                <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-primary-soft text-primary">
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="mt-3 font-display text-base font-extrabold text-primary">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground/75">{text}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 rounded-md bg-muted/60 p-5">
            <h3 className="font-display text-lg font-extrabold text-foreground">Så fungerar tjänsten</h3>
            <ol className="mt-3 space-y-3">
              {HOW.map((t, i) => (
                <li key={t} className="flex items-start gap-3 text-sm text-foreground/85">
                  <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                    {i + 1}
                  </span>
                  {t}
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}
